import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
    tagHotjarRecording,
    Status,
    formatPhoneString,
} from '../../helpers/util'
import ErrorAlert from '../../components/views/includes/ErrorAlert'
import { logErrorOnServer } from '../../helpers/serverLogging'
import useProfile from '../../hooks/useProfile'
import useAuth from '../../hooks/useAuth'
import useRequest from '../../hooks/useRequest'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import ButtonAs from '../../components/Button/Button'
import PersonalDetails from './components/forms/PersonalDetails'
import { isNil } from 'ramda'
import Notificationpreferences from './components/forms/NotificationPreferences'
import PrimaryLocation from './components/forms/PrimaryLocation'
import LocationPreferences from './components/forms/LocationPreferences'
import AppRoutes from '../../routes/appRoutes'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useUsers from '../../hooks/useUsers'
import { hasPendingRequests } from '../../utils/pendingRequestHelpers'
import useAlert from '../../hooks/useAlert'
import useAdmin from '../../hooks/useAdmin'
import { removeStepsAndRequests } from '../../utils/handleOnDone'
import { useNetworkStatus } from '../../utils/networkStatus'
import { deleteAllPendingRequests } from '../../../../indexedDb/pendingRequests'
import { deleteAllDashboardData } from '../../utils/deleteIndexDbData'

const logError = logErrorOnServer('Profile')

const RETURN_TO_ADMIN_FAILED_MESSAGE =
    'Return to admin failed. Sign out and sign in again to return to the admin site.'

const ProfilePage = ({ history }) => {
    const [error, setError] = useState('')
    const [returnToAdminFailed, setReturnToAdminFailed] = useState(false)
    const [formStatus, setFormStatus] = useState(Status.INIT)
    const [formError, setFormError] = useState('')
    const { handleSaveProfile } = useProfile()
    const {
        handleProfileSuccess,
        user,
        impersonated,
        impersonatedId,
        handleStopImpersonationSuccess,
    } = useAuth()
    const { stopImpersonation } = useAdmin()
    const userName = `${user?.first_name}  ${user?.last_name}`
    const userCompanyName = user?.company?.name
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { isOffline } = useNetworkStatus()

    const {
        changeNeedsUpdate,
        changeNeedsUpdatePM,
        resetAllFilters,
    } = useRequest()
    const {
        getProfileInfo,
        saveUserCompanyInfo,
        saveUserLocationPreferences,
        toggleNeedReloadProfile,
        needReloadProfile,
    } = useUsers()

    const mapLocations = locations => {
        return (
            locations?.map(item => {
                return {
                    ...item,
                    address: `${item?.shipping_street1}, ${item?.shipping_city}, ${item?.shipping_state} ${item?.shipping_zip}`,
                    id: item?.id,
                    label: item?.name,
                    value: item?.external_id,
                }
            }) || []
        )
    }

    const userCompanyId = user.company.id

    const selectOptions = mapLocations(user?.company?.shiptos)

    const onGetProfileInfoSuccess = response => {
        let session = JSON.parse(localStorage.getItem('session'))

        const userCompany = response?.data?.company
        if (userCompany && userCompany?.shiptos?.length > 0) {
            saveUserCompanyInfo(userCompany)
            session.user.company = userCompany
        }
        const userLocationPreferences = response?.data?.locationPreferences
        if (userLocationPreferences && userLocationPreferences.length > 0) {
            saveUserLocationPreferences(userLocationPreferences)
            session.user.locationPreferences = userLocationPreferences
        }

        const sessionObj = JSON.stringify(session)
        localStorage.setItem('session', sessionObj)
    }

    const onGetProfileInfoError = error => {
        console.log(error)
        const message = error?.response?.data
        logError(message)
    }

    useEffect(() => {
        if (needReloadProfile) {
            getProfileInfo(onGetProfileInfoSuccess, onGetProfileInfoError)
            toggleNeedReloadProfile(false)
        }
    }, [])

    useEffect(() => {
        setFormStatus(Status.LOADING)
        tagHotjarRecording('Edit Profile')

        return () => {
            setFormError(null)
            setFormStatus('')
            setError('')
        }
    }, [userCompanyId])

    const onUserProfileSavedSuccess = response => {
        changeLoading(false)
        //put into local storage
        var session = JSON.stringify(response.data.session)
        localStorage.setItem('session', session)

        setFormStatus(Status.SUCCESS)
        changeNeedsUpdate({ dashboard: true, request: true })
        changeNeedsUpdatePM({ plannedMaintenance: true })
        resetAllFilters()
        handleProfileSuccess(response.data.session)
    }

    const onUserProfileSavedError = error => {
        changeLoading(false)
        tagHotjarRecording('API Error')
        const {
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}
        setFormError(message)
        setFormStatus(Status.ERROR)
        logError(message)
    }

    const handleSaveUserInfo = userSettingsToSave => {
        changeLoading(true)

        const userToSave = {
            ...user,
            locationPreferences: user?.locationPreferences?.map(item => {
                return `${item?.id}`
            }),
        }

        const hasPhone = !isNil(userSettingsToSave?.phone)
        const newUserInfo = {
            id: user.id,
            email: user.email,
            ...userToSave,
            ...userSettingsToSave,
            phone: hasPhone
                ? formatPhoneString(userSettingsToSave?.phone)
                : user?.phone,
        }
        return handleSaveProfile(
            newUserInfo,
            onUserProfileSavedSuccess,
            onUserProfileSavedError,
        )
    }

    // if (formStatus === Status.ERROR) {
    //     window.scrollTo(0, 0)
    // }

    const handleSavePersonalInfo = formValues => {
        return handleSaveUserInfo(formValues)
    }

    const renderContentPersonalInfo = () => {
        return (
            <PersonalDetails
                user={user}
                handleSubmit={handleSavePersonalInfo}
            />
        )
    }

    const renderContentNotificationPreferences = () => {
        return (
            <Notificationpreferences
                user={user}
                handleSubmit={handleSavePersonalInfo}
            />
        )
    }

    const renderContentPrimaryLocation = () => {
        return (
            <PrimaryLocation
                user={user}
                handleSubmit={handleSavePersonalInfo}
                shiptos={selectOptions}
            />
        )
    }

    const renderContentLocationPreferences = () => {
        return (
            <LocationPreferences
                user={user}
                handleSubmit={handleSavePersonalInfo}
                shiptos={selectOptions}
            />
        )
    }

    const handleDeleteAll = async () => {
        try {
            await deleteAllPendingRequests()
        } catch (error) {
            console.error('Failed to delete pending requests:', error)
        }
    }

    const clearSessionStorage = () => {
        removeStepsAndRequests()
        handleDeleteAll()
        localStorage.removeItem('authToken')
        localStorage.removeItem('session')
        localStorage.removeItem('expirationTime')
        localStorage.removeItem('reviewRequests')
        localStorage.removeItem('lastLogin')
        localStorage.removeItem('timeToRefresh')
        deleteAllDashboardData()
    }

    const handleOfflineSessionCheck = () => {
        clearSessionStorage()
        handleChangeOpen(false)
        if (isOffline) {
            window.location.reload()
        } else {
            history?.push(AppRoutes.logout())
        }
    }

    const signOut = async history => {
        const pendingRequests = await hasPendingRequests()
        if (!pendingRequests && !isOffline) {
            history?.push(AppRoutes.logout())
        } else if (!pendingRequests && isOffline) {
            clearSessionStorage()
            window.location.reload()
        } else {
            handleChangeAlert({
                title: 'Are you sure?',
                message:
                    'Your pending requests will be lost if you don’t log in again soon',
                type: 'error',
                textOk: 'Yes, log out',
                textCancel: 'No, cancel',
                onDone: () => {
                    handleOfflineSessionCheck()
                },
                onCancel: () => handleChangeOpen(false),
            })
        }
    }

    const handleReturnToAdmin = () => {
        if (returnToAdminFailed) {
            return
        }
        changeLoading(true)
        stopImpersonation(impersonatedId)
            .then(response => {
                if (response?.data?.session) {
                    setReturnToAdminFailed(false)
                    handleStopImpersonationSuccess(response.data.session)
                    history.push(AppRoutes.adminHome())
                } else {
                    setReturnToAdminFailed(true)
                }
            })
            .catch(() => {
                setReturnToAdminFailed(true)
            })
            .finally(() => {
                changeLoading(false)
            })
    }

    return (
        <ContentLayout>
            {(error || formStatus === Status.ERROR) && (
                <ErrorAlert error={formError ? formError : error} />
            )}
            {returnToAdminFailed && (
                <ErrorAlert error={RETURN_TO_ADMIN_FAILED_MESSAGE} />
            )}
            <MainHeading
                title={userName}
                subtitle={userCompanyName}
                actions={[
                    ...(impersonated
                        ? [
                              <ButtonAs
                                  onClick={handleReturnToAdmin}
                                  variant="tertiary"
                                  text="Return to Admin"
                                  key="impersonatingButton"
                                  className="lg:w-auto w-full"
                                  disabled={returnToAdminFailed}
                              />,
                          ]
                        : []),
                    <ButtonAs
                        onClick={() => signOut(history)}
                        variant="error"
                        text="Sign Out"
                        key="signOutButton"
                        className="lg:w-auto w-full"
                    />,
                ]}
            />
            <div className="lg:w-220 w-full lg:mt-12 lg:p-0 p-4 h-auto m-auto flex flex-col items-center justify-start lg:gap-y-6 gap-4 lg:pb-2">
                {renderContentPersonalInfo()}
                {renderContentNotificationPreferences()}
                {renderContentPrimaryLocation()}
                {renderContentLocationPreferences()}
            </div>
        </ContentLayout>
    )
}

ProfilePage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
}

export default ProfilePage
